import { getQueryParams, setQueryParams } from './utils.js';
import { resolveTemplate } from './template/resolveTemplate.js';
import { EventObject } from "../events/EventObject.js";

export { Router }

// initialize timestamp for cache busting on initial loading
const initialTimestamp = new Date().getTime();

/**
 * Class for ajax based single-page navigation.
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.utils
 */
class Router extends EventObject {

    /**
     * @param {string} id id of the target DOM-Element
     * @param {Object} config configuration of existing sites
     * 
     * config = {
     *   sites: []              // configuration for each site
     *   defaultSite: ""        // default site (home), if no site is defined
     *   errorTemplate: ""      // error page if the site cannot load
     *   defaultMetadata: {}    // default fallback metadata if the page does not have specific metadata
     *   initState: boolean     // set the initial URL state (necessary when content was already loaded using php),
     *   disableCache: boolean  // if true: adds a timestamp to ajax requests to prevent the browser from using the cache
     *   endpointTemplate: ""   // url template for requesting sites using util.fillTemplate syntax
     * }
     */
    constructor(id, config) {
        super({
            'preProcess': [],
            'postProcess': []
        });

        this._id = (id.startsWith("#")) ? id : ("#" + id);
        this._container = null;
        this._config = config;

        this._controller = null;
        this._navigationBlocked = false;
        this._url = window.location.href;
        this._params = {};
        this._controllerCache = {};

        if (this._config.initState) {
            this._params = getQueryParams();
            this._params.site = this._checkSite(this._params.site);
        }

        const onLoad = () => {
            if (this._config.initState) {
                const template = this._config.sites[this._params.site];
                if (template.script && !this._runController(this._params.site)) {
                    this._loadScript(template.script.src, template.script.type)
                        .finally(() => this._runController(this._params.site));
                }
            }
            window.addEventListener('popstate', (e) => {
                if (this._checkQueryChange()) this.navigate();
            });
            document.removeEventListener("DOMContentLoaded", onLoad);
        }

        document.addEventListener("DOMContentLoaded", onLoad);
    }

    /**
     * Checks if the URL query was changed. Ignores url hash changes.
     * @private
     */
    _checkQueryChange() {
        const url = window.location.href;
        return (url.split("#")[0] != this._url.split("#")[0]);
    }

    /**
     * clear the container content and insert the page content
     * @private
     */
    _showContent(content) {
        if (!this._container) this._container = document.querySelector(this._id);
        this._container.innerHTML = content;
        window.scrollTo(0, 0);

        // execute from inserted html
        const scripts = this._container.querySelectorAll("script");
        for (let i = 0; i < scripts.length; ++i) {
            Function(scripts[i].innerHTML)();
        }
    }

    /**
     * create a script tag for a javascript file and append it to the container
     * @private
     */
    _loadScript(src, type) {
        return new Promise(resolve => {
            if (!this._container) this._container = document.querySelector(this._id);
            const script = document.createElement("script");
            script.onload = () => resolve();
            script.src = (this._config.disableCache) ? this._appendTime(src) : src;
            if (type) script.type = type;
            this._container.appendChild(script);
        });
    }

    /**
     * check if a site exists. returns default site if site is not found
     * @private
     */
    _checkSite(site) {
        if (Array.isArray(site)) site = site[0];
        if (this._config && !this._config.sites[site]) site = this._config.defaultSite;
        return site;
    }

    /**
     * run a page controller
     * 
     * @returns {boolean} controller was initialized
     * @private
     */
    _runController(site) {
        if (!this._controller && (site in this._controllerCache)) {
            const params = this.getParams();
            const config = this._config.sites[site].config;

            this._controller = new this._controllerCache[site](params, config);
            this._controller.update(params);
            return true;
        }
        return false;
    }

    /**
     * append integer timestamp to urls
     * @private
     */
    _appendTime(url) {
        return url + "?t=" + initialTimestamp;
    }

    /**
     * getting the url hash
     * @private
     */
    _getHash(url) {
        url = url || window.location.href
        const splitUrl = url.split("#");
        return (splitUrl.length == 2) ? splitUrl[1] : "";
    }

    /**
     * @returns {Object} current URL-Params used by the router
     */
    getParams() {
        // deep copy the object
        return JSON.parse(JSON.stringify(this._params));
    }

    /**
     * Set the site specific javascript controller for the currently active site
     * Site classes will get cached to support loading them from a module
     * which would only get evaluated once.
     * 
     * @param {Site} controller Instance of the controller
     */
    registerController(controller) {
        if (this._params.site) this._controllerCache[this._params.site] = controller;
    }

    /**
     * Register a site specific script e.g. for loading the controller
     * for the currently active site
     * 
     * @param {string} src path to the script
     * @param {string} type (e.g. module)
     */
    registerScript(src, type) {
        if (this._params.site in this._config.sites) {
            this._config.sites[this._params.site].script = {
                src: src,
                type: type || ""
            }
        }
    }

    /**
     * Set page metadata
     * 
     * @param {Object} metadata 
     */
    setMetadata(metadata) {
        metadata = metadata || {};
        if (this._config && this._config.defaultMetadata) {
            metadata = Object.assign({}, this._config.defaultMetadata, metadata);
        }

        // shorthand function for setting meta tags
        function setMeta(name, content) {
            let meta = document.head.querySelector(`meta[name="${name}"]`);
            if (content) {
                if (!meta) {
                    meta = document.createElement("meta");
                    meta.setAttribute("name", name);
                    document.head.appendChild(meta);
                }   
                meta.setAttribute('content', content);
            } else if (meta) {
                meta.remove();
            }
        }

        // set basic metadata
        document.title = metadata.title;
        setMeta("title", metadata.title);
        setMeta("DC.Title", metadata.title);
        setMeta("keywords", metadata.keywords);
        setMeta("DC.Subject", metadata.keywords);
        setMeta("description", metadata.description);
        setMeta("DC.Description", metadata.description);
        setMeta("DC.Language", metadata.language);
        setMeta("DC.Publisher", metadata.publisher);

        // set ldJson
        let ldJson = document.head.querySelector('#ldJson');
        if (metadata.ldJson) {
            if (!ldJson) { 
                ldJson = document.createElement("script");
                ldJson.setAttribute("id", "ldJson");
                ldJson.setAttribute("type", "application/ld+json");
                document.head.appendChild(ldJson);
            }
            ldJson.innerHTML = JSON.stringify(metadata.ldJson);
        } else if (ldJson) {
            ldJson.remove();
        }
    }

    /**
     * display the default error template defined in the router configuration
     */
    displayError() {
        const url = (this._config.disableCache) ? this._appendTime(this._config.errorTemplate) : this._config.errorTemplate;
        fetch(url).then(response => response.text()).then(text => this._showContent(text));
    }

    /**
     * Execute single page navigation using target URL-Parameters.
     * 
     * @param {Object | string} params target URL-Params or an url     
     * @param {string} hash url hash (can also be included in params if a string is passed)
     */
    navigate(params, hash) {
        if (this._navigationBlocked) return;
        this._navigationBlocked = true;

        const previousSite = this._params.site;

        // set params and hash
        if (typeof params == "string") {
            this._params = getQueryParams(params);
            hash = hash || this._getHash(params);
        } else if (typeof params == "object") {
            this._params = params;
        } else {
            this._params = params || getQueryParams();
            hash = hash || this._getHash();
        }

        this._params.site = this._checkSite(this._params.site);
        const template = this._config.sites[this._params.site];
        this._url = location.origin + location.pathname + setQueryParams(this._params) + (((typeof hash == "string") && hash.trim().length) ? `#${hash.trim()}` : "");
        const eventParams = this.getParams();

        this.fire("preProcess", { params: eventParams, isRestored: !params });

        // called at the end
        const finished = responseText => {
            if (responseText) {
                this._showContent(responseText);
                if (template.script && !this._runController(this._params.site)) {
                    this._loadScript(template.script.src, template.script.type)
                        .finally(() => this._runController(this._params.site));
                }
            }

            if (params) {
                history.pushState(null, null, this._url);
            } else {
                history.replaceState(null, null, this._url);
            }

            this.setMetadata(template.metadata);
            this.fire("postProcess", { params: eventParams, isRestored: !params });
            this._navigationBlocked = false;
        };

        const load = () => {
            const path = ("path" in template) ? template.path : resolveTemplate(this._config.endpointTemplate, { "name": this._params.site });
            const url = (this._disableCache) ? this._appendTime(path) : path;
            fetch(url).then(response => response.text()).then(finished)
                .catch(() => {
                    this.displayError();
                    finished();
                });
        }

        if (previousSite != this._params.site) {
            let promise = null;
            if (this._controller) {
                promise = this._controller.dispose();
                this._controller = null;
            }

            if (promise instanceof Promise) {
                promise.finally(() => load());
            } else {
                load()
            }
        } else {
            if (this._controller) this._controller.update(eventParams);
            finished();
        }
    }

};